import type { CustomHeaders } from "@aws-amplify/data-schema/runtime";
import { fetchEventSource } from "@microsoft/fetch-event-source";

import type { AmplifyOutputs } from "aws-amplify/adapter-core";
import { fetchAuthSession } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/data";

import amplifyOutputs from "@/backend-data-amplify-config.json";
import type { Schema } from "@/backend-data-schema";
import type {
  MobyToolNames,
  MobyToolNamesLMP,
  MobyToolNamesDashboard,
  MobyToolNamesGlobal,
} from "@/backend-types";

let cachedAmplifyOutputs: AmplifyOutputs | undefined;
/**
 * @returns The Amplify outputs from the backend.
 */
export async function getAmplifyOutputs(): Promise<AmplifyOutputs | undefined> {
  if (cachedAmplifyOutputs !== undefined) {
    return cachedAmplifyOutputs;
  }
  if (Object.keys(amplifyOutputs).length > 0) {
    cachedAmplifyOutputs = amplifyOutputs;
    return amplifyOutputs;
  }
  if (import.meta.env.VITE_AMPLIFY_OUTPUTS_URL) {
    try {
      const response = await fetch(import.meta.env.VITE_AMPLIFY_OUTPUTS_URL);
      const object = await response.json();
      if (Object.keys(object).length > 0) {
        cachedAmplifyOutputs = object;
        return object;
      }
    } catch {
      //TODO: once backend is properly setup this should be removed as we want to be notified of any errors fetching the config
      // but for now, we can just return undefined
      return undefined;
    }
  }
  return undefined;
}

export type AmplifyBackendClient = ReturnType<typeof generateClient<Schema>>;
export type {
  Schema,
  MobyToolNames,
  MobyToolNamesDashboard,
  MobyToolNamesGlobal,
  MobyToolNamesLMP,
};

export const amplifyBackendClient = generateClient<Schema>({
  authMode: "userPool",
});

export const publicAmplifyBackendClient = generateClient<Schema>({
  authMode: "apiKey",
  async headers() {
    const headers: CustomHeaders = {};

    const authorization = top?.window._msAuth?.session?.authorization;
    if (authorization && authorization.match(/^(jwt-as|pat):/)) {
      headers.Authorization = `Bearer ${authorization}`;
      return headers;
    }
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken.toString();
    if (accessToken) {
      headers.Authorization = accessToken;
    }
    return headers;
  },
});

type SSEEvent = {
  data: string;
  event?: string;
  id?: string;
};

type AuthenticatedEventSourceOptions = {
  url: string;
  method?: "GET" | "POST";
  body?: Record<string, unknown>;
  onEvent?: (eventType: string, event: SSEEvent) => void;
  onOpen?: (response: Response) => void;
  onError?: (error: unknown) => void;
  onClose?: () => void;
  signal?: AbortSignal;
};

export async function createAuthenticatedEventSource({
  url,
  method = "GET",
  body,
  onEvent,
  onOpen,
  onError,
  onClose,
  signal,
}: AuthenticatedEventSourceOptions): Promise<void> {
  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken.toString();

  const headers: Record<string, string> = accessToken
    ? { Authorization: accessToken }
    : {};

  if (method === "POST" && body) {
    headers["Content-Type"] = "application/json";
  }

  try {
    await fetchEventSource(url, {
      method,
      headers,
      body: method === "POST" && body ? JSON.stringify(body) : undefined,
      signal,
      onmessage(msg) {
        if (onEvent) {
          const eventType = msg.event || "message";
          onEvent(eventType, {
            data: msg.data,
            event: msg.event,
            id: msg.id,
          });
        }
      },
      async onopen(response) {
        if (onOpen) {
          await Promise.resolve(onOpen(response));
        }
      },
      onerror(error) {
        if (onError) {
          onError(error);
        }
        throw error;
      },
      onclose() {
        if (onClose) {
          onClose();
        }
      },
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return;
    }
    throw error;
  }
}
